import { propertyFetch } from './idxPropertyClient';
import { SITE_CONFIG } from '../config';
import { getChatLeads, submitChatLead } from './chatLeadService';
import { getConfirmedContact, normalizeContact, isCompleteContact, rememberConfirmedContact, readSaveStorage, writeSaveStorage, PENDING_KEY, setFavorite } from './propertySaveStorage';

const requests = new Map();
const leadRequests = new Map();
const HEADERS = { 'X-API-Key': 'treas-api-key-2025' };
const API = 'https://tophamtechbe.com/api/treas/idx/v1';
export const listingSnapshot = (p = {}) => ({
  listingKey: String(p.listing_key ?? p.listingKey ?? '').trim(),
  price: String(p.current_price ?? p.price ?? ''),
  area: String(p.location?.city ?? p.city ?? p.area ?? ''),
  postalCode: String(p.location?.postal_code ?? p.postal_code ?? p.postalCode ?? ''),
  state: String(p.location?.state ?? p.state ?? p.state_province ?? '')
});
const completeListing = async (property) => {
  const snapshot = listingSnapshot(property);
  if (!snapshot.listingKey) throw new Error('This home is missing its listing key. Please reopen the listing.');
  if (snapshot.price !== '' && snapshot.area) return snapshot;
  // Use the existing listing snapshot first. Enrich only missing fields, without blocking a valid save on enrichment failure.
  try {
    const privateId = snapshot.listingKey.startsWith('PRIVATE_') ? snapshot.listingKey.slice(8) : null;
    const url = privateId
      ? `https://tophamtechbe.com/api/treas/private/v1/listing/${encodeURIComponent(privateId)}?site_id=${encodeURIComponent(SITE_CONFIG.SITE_ID)}`
      : `${API}/retrieve-property-summary?listingKey=${encodeURIComponent(snapshot.listingKey)}`;
    const response = await propertyFetch(url, { headers: privateId ? HEADERS : {} });
    if (response.ok) {
      const data = await response.json();
      const p = privateId ? (data.listing || data) : data.properties?.[0];
      if (p) {
        const extra = listingSnapshot(privateId ? { ...p, current_price: p.listing_type === 'sale' ? p.price_sale : p.price_lease_monthly } : p);
        for (const key of ['price', 'area', 'postalCode', 'state']) if (snapshot[key] === '') snapshot[key] = extra[key];
      }
    }
  } catch { /* The API accepts incomplete optional listing fields. */ }
  return snapshot;
};
const ensureLead = async (contact, listing) => {
  if (leadRequests.has(contact.email)) return leadRequests.get(contact.email);
  const ensure = async () => {
    const known = getConfirmedContact();
    if (known?.email === contact.email && known.chatLeadId) return known.chatLeadId;
    // Also recover a previous successful POST whose response was lost, or a lead captured in another browser.
    const response = await getChatLeads({ email: contact.email, siteId: SITE_CONFIG.SITE_ID });
    if (!response.ok) throw new Error('We could not check your contact details. Please try saving again.');
    const leads = await response.json();
    if (!Array.isArray(leads)) throw new Error('We could not confirm your contact details. Please try again.');
    const existing = leads.find(lead => lead.id && String(lead.email || '').trim().toLowerCase() === contact.email && lead.siteId === SITE_CONFIG.SITE_ID);
    if (existing) { rememberConfirmedContact(contact, existing); return existing.id; }
    const created = await submitChatLead({ ...contact, ...listing, siteId: SITE_CONFIG.SITE_ID, serviceReq: 'buying' }, { rememberContact: true });
    if (!created.ok || created.honeypotBlocked) throw new Error('We could not save your contact details. Please try again.');
    const lead = await created.json();
    if (!lead?.id) throw new Error('We could not confirm your contact details. Please try again.');
    rememberConfirmedContact(contact, lead);
    return lead.id;
  };
  const promise = (typeof navigator !== 'undefined' && navigator.locks?.request
    ? navigator.locks.request(`property-lead-${SITE_CONFIG.SITE_ID}-${contact.email}`, ensure)
    : ensure()).finally(() => leadRequests.delete(contact.email));
  leadRequests.set(contact.email, promise);
  return promise;
};
export const getPendingSaves = () => readSaveStorage(PENDING_KEY, {});
export const savePropertyInterest = (property, value) => {
  const contact = normalizeContact(value);
  const listing = listingSnapshot(property);
  if (!isCompleteContact(contact)) return Promise.reject(new Error('Please enter your name, a valid email, and phone number.'));
  if (!listing.listingKey) return Promise.reject(new Error('This home is missing its listing key. Please reopen the listing.'));
  const key = `${contact.email}:${listing.listingKey}`;
  if (requests.has(key)) return requests.get(key);
  const run = async () => {
    writeSaveStorage(PENDING_KEY, { ...getPendingSaves(), [key]: { contact, listing } });
    setFavorite(listing.listingKey, true);
    const complete = await completeListing(property);
    await ensureLead(contact, complete);
    const params = new URLSearchParams({ ...contact, listingKey: complete.listingKey, price: complete.price, area: complete.area, siteId: SITE_CONFIG.SITE_ID });
    const response = await fetch(`${API}/interested-buy?${params}`, { method: 'POST', headers: HEADERS });
    if (!response.ok) throw new Error('Your home is saved in this browser, but we could not send your interest. Please retry.');
    const pending = { ...getPendingSaves() }; delete pending[key];
    writeSaveStorage(PENDING_KEY, pending);
    return { contact, listing: complete };
  };
  const promise = run().finally(() => requests.delete(key));
  requests.set(key, promise);
  return promise;
};
